import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  InputAdornment,
  TextField,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  Check as CheckCircleIcon,
  Home,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchOrders, searchOrders } from '../../redux/slices/orderSlice';
import OrdersTable, { OrderData } from '../../components/DataTable/examples/OrdersTable';
import { Order } from '../../types';
import BackButton from '../../components/BackButton/BackButton';
import styles from './OrdersPage.module.css';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import * as orderService from '../../services/order-service';
import { useSnackbar } from 'notistack';

// Extended Order interface with additional properties needed
interface ExtendedOrder extends Order {
  customer?: {
    company_name: string;
    [key: string]: unknown;
  };
  quote?: {
    id: string;
    code: string;
    status: string;
    expiration_date: string;
    [key: string]: unknown;
  };
  created_at?: string;
  order_items?: Array<{
    product_id: string;
    quantity: number;
    unit_price: number;
    discount_percent?: number;
    discount_amount?: number;
    id: string;
    order_id: string;
  }>;
}

const OrdersPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { items: orders, loading, error } = useAppSelector(state => state.orders);

  useEffect(() => {
    dispatch(fetchOrders());
    setBreadcrumbs([
      { label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> },
      { label: 'Pedidos' },
    ]);
  }, [dispatch, setBreadcrumbs]);

  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim() === '') {
      dispatch(fetchOrders());
    } else {
      debounce(() => {
        dispatch(searchOrders(value));
      }, 500)();
    }
  };

  const handleViewOrder = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  // Helper function to calculate total
  const calculateTotal = (order: ExtendedOrder): string => {
    if (!order.order_items || order.order_items.length === 0) {
      return '0,00';
    }

    const total = order.order_items.reduce((sum, item) => {
      return sum + item.quantity * item.unit_price;
    }, 0);

    // Format as Brazilian currency
    return total.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Transform order data for table
  const orderTableData: OrderData[] = orders.map(order => {
    const extendedOrder = order as ExtendedOrder;
    return {
      id: order.id,
      client: extendedOrder.customer?.company_name || 'Cliente não identificado',
      code: order.id.substring(0, 8),
      quoteCode: extendedOrder.quote?.code || order.quote_id?.substring(0, 8) || 'N/A',
      date: new Date(extendedOrder.created_at || '').toLocaleDateString('pt-BR'),
      paymentMethod: order.payment_method,
      paymentTerm: order.payment_term,
      status: order.status,
      value: 'R$ ' + calculateTotal(extendedOrder),
    };
  });

  // Filter orders based on search term
  const filteredOrders = orderTableData.filter(order => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      order.client.toLowerCase().includes(searchTermLower) ||
      order.code.toLowerCase().includes(searchTermLower) ||
      order.quoteCode.toLowerCase().includes(searchTermLower) ||
      order.status.toLowerCase().includes(searchTermLower)
    );
  });

  return (
    <>
      {/* Back button */}
      <BackButton />

      {/* Header section */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: { xs: '100%', md: '60%' },
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}
            >
              Pedidos: acompanhe e gerencie suas compras em tempo real.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Nesta página você encontra todos os seus pedidos ativos, podendo acompanhar status,
              datas de entrega e detalhes dos produtos. Gerencie o status de cada pedido e tenha
              visibilidade completa do ciclo de compra.
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: '100%', md: '40%' },
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              mt: { xs: 1, md: 0 },
            }}
          >
            <Button
              component={Link}
              to="/quotes/new"
              variant="contained"
              fullWidth={isMobile}
              sx={{
                bgcolor: 'var(--acer-dark)',
                '&:hover': { bgcolor: '#333' },
                textTransform: 'none',
                borderRadius: '1px',
                color: 'white',
                height: '40px',
                maxHeight: '40px',
              }}
            >
              Criar nova cotação
            </Button>
          </Box>
        </Box>
        {/* Tip box */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 1 },
            bgcolor: '#fafff6',
            p: 2,
            borderRadius: 1,
            mt: 2,
          }}
        >
          <CheckCircleIcon
            sx={{
              color: '#2E7D32',
              fontSize: 16,
              border: '1px solid #2E7D32',
              borderRadius: '50%',
              mt: { xs: 0.5, sm: 0 },
            }}
          />
          <Typography variant="body2" sx={{ color: '#2E7D32' }}>
            <span style={{ fontWeight: 500 }}>Dica:</span> Clique em qualquer pedido para visualizar
            todos os detalhes, incluindo produtos, endereço de entrega e informações de pagamento.
          </Typography>
        </Box>
      </Box>

      {/* Search and action section */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 3,
        }}
      >
        <TextField
          placeholder={
            isMobile ? 'Pesquisar pedidos...' : 'Pesquise por número do pedido, cliente ou status'
          }
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearch}
          sx={{
            flexGrow: 1,
            width: { xs: '100%', sm: 'auto' },
            maxWidth: '100%',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* DataTable section */}
      <Paper sx={{ borderRadius: 1, overflow: 'hidden', p: { xs: 1, sm: 2 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <img
            src="/icons/ultimos-pedidos.svg"
            alt="Opportunity icon"
            style={{
              width: 24,
              height: 24,
              marginRight: '8px',
            }}
          />
          <Typography
            variant="h6"
            sx={{ fontWeight: 500, color: '#2b3445', fontSize: { xs: '16px', sm: '18px' } }}
          >
            Meus pedidos
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Carregando pedidos...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
            <Typography>Erro ao carregar pedidos: {error}</Typography>
          </Box>
        ) : filteredOrders.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Nenhum pedido encontrado.</Typography>
            <Button
              component={Link}
              to="/quotes/new"
              variant="contained"
              sx={{
                mt: 2,
                bgcolor: 'var(--acer-dark)',
                '&:hover': { bgcolor: '#333' },
                textTransform: 'none',
              }}
            >
              Criar uma cotação
            </Button>
          </Box>
        ) : (
          <OrdersTable data={filteredOrders} onViewOrder={handleViewOrder} />
        )}
      </Paper>
    </>
  );
};

export default OrdersPage;
